
import { Button, Form, Input, Modal } from "antd";
import Text from "antd/es/typography/Text";
import logoCCBJI from "@/assets/img/2800_CCBJI_Logo.bmp";
import logoFVJ from "@/assets/img/2801_FVJ_Logo.bmp";
import logo from "@/assets/img/logo_offical.png";
import { useNavigate, useParams } from "react-router-dom";
import { Fragment, useEffect, useRef, useState } from "react";
import { SizeType } from "antd/es/config-provider/SizeContext";
import { getAuthInfo } from "@/features/homepage/api/ApiGetAuthInfo";
import storage from "@/utils/storage";
import { RESPONSE_ERR, ERR_MESSAGE } from "@/constant";
import { LoadingSpinner } from "@/components/SpinnerOverlay";
import { signUp } from "@/features/homepage/api/ApiSignUp";


const Login = () => {
    const navigate = useNavigate();
    const [showSystemError, setSystemError] = useState("");
    const [size, setSize] = useState<SizeType>("large");
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const token_param = useParams();
    const inputRef = useRef(null);
    const [isModalSignUpOpen, setIsModalSignUpOpen] = useState(false);
    const [isModalLoginOpen, setIsModalLoginOpen] = useState(false);
    const [isModalIncorrectURLLoginOpen, setIsModalIncorrectURLLoginOpen] = useState(false);
    const titleLoginOpen = <>初期登録が完了していません。<br />初期登録ボタンから登録を完了してください。</>;
    const titleSignUpOpen = <>既にご登録いただいてます。<br />ログインボタンからログインしてください。</>;
    const titleIncorrectURLLoginOpen = <>メールアドレス登録時に送付したメールに添付しているURLよりアクセスしてください。<br />メールが届いていない場合やリンクの期限が切れている場合は再度初回登録を行ってください。</>;

    useEffect(() => {
        focusID();
        const userId = storage.getId();
        const token = storage.getToken();
        if (userId && token) {
            navigate("/");
        }
    }, [navigate]);

    // Function to focus on the ID input field
    const focusID = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }

    // Prepare login request payload based on form values and token param
    const CheckKna1Info = (values: { id?: string; password?: string }) => ({
        login_id: values.id || null,
        password: values.password || null,
        token: token_param.token ? token_param.token : null
    });

    // Prepare sign-up request payload based on form values
    const CheckSignUpInfo = (values: { id?: string; password?: string }) => ({
        login_id: form.getFieldValue("id") || null,
        password: form.getFieldValue("password") || null,
        token: token_param.token ? token_param.token : null
    });

    // Redirect to password reset page
    const goToResetPassword = () => {
        navigate("/reset-password");
    };

    // Open help link for ID inquiry in a new tab
    const goToHelpPC = () => {
        window.open('https://www.ccbji.co.jp/inquiry/form/?contact_type=3', '_blank')
    };

    // Handle login form submission
    const Ok = async (values: any) => {
        setLoading(true);
        setSystemError("");

        const dataRequest = CheckKna1Info(values);

        try {
            // Call authentication API
            const response = await getAuthInfo(dataRequest);

            setLoading(false);
            const redirectMap: { [key: string]: string } = {
                E0104: "/create-password",
                E0106: "/token-expired"
            };

            const shouldRedirect = (code: string) => Object.prototype.hasOwnProperty.call(redirectMap, code);

            if (response.error_code) {
                const errorCode = response.error_code;

                if (errorCode === RESPONSE_ERR.E0104) {
                    storage.setToken(response.access_token);
                    storage.set(dataRequest.login_id, 'id');
                    storage.set(errorCode, 'type')
                }

                if (shouldRedirect(errorCode)) {
                    navigate(redirectMap[errorCode], {
                        state: {
                            id: dataRequest.login_id ?? null,
                            dealer_cd: dataRequest.password ?? null
                        }
                    });
                }

                if (errorCode === RESPONSE_ERR.WAI005) {
                    setSystemError(ERR_MESSAGE.WAI005_MESSAGE);
                }
                return
            }

            // Successful login: store tokens and redirect to home
            if (response.access_token && !response.error_code) {
                storage.setToken(response.access_token);
                storage.set(dataRequest.login_id, 'id');
                storage.setRefreshToken(response.refresh_token);

                navigate("/");
                return;
            }

        } catch (error) {
            setLoading(false);
            // Show error message based on error code
            const error_code = error.response.data.error_code;
            const error_msg = error.response.data.error_msg;

            if (error_code === RESPONSE_ERR.WAI001) {
                setSystemError(ERR_MESSAGE.WAI001_MESSAGE);
            } else if (error_code === RESPONSE_ERR.WAI002) {
                setSystemError(ERR_MESSAGE.WAI002_MESSAGE);
            } else if (error_code === RESPONSE_ERR.WAI003) {
                setSystemError(ERR_MESSAGE.WAI003_MESSAGE);
            } else if (error_code === RESPONSE_ERR.WAS001) {
                setSystemError(ERR_MESSAGE.WAS001_MESSAGE);
            } else if (error_code === RESPONSE_ERR.WAS002) {
                setSystemError(ERR_MESSAGE.WAS002_MESSAGE);
            } else if (error_code === RESPONSE_ERR.WAS003) {
                setSystemError(ERR_MESSAGE.WAS003_MESSAGE);
            } else if (error_code === RESPONSE_ERR.WAI008) {
                setIsModalLoginOpen(true);
            } else if (error_code == RESPONSE_ERR.WAI009) {
                setIsModalIncorrectURLLoginOpen(true);
            } else if (error_code === RESPONSE_ERR.WAI005) {
                setSystemError(ERR_MESSAGE.WAI005_MESSAGE);
            } else {
                setSystemError(ERR_MESSAGE.WAS001_MESSAGE);
            }
            return
        }

    }

    // Handle sign-up button click
    const Signup = async (values: any) => {
        const validate = await form.validateFields();
        setLoading(true);
        const data_input = CheckSignUpInfo(values);
        try {
            // Call sign-up API
            const res = await signUp(data_input);
            if (res.error_code) {
                const errorCode = res.error_code;
                if (errorCode === RESPONSE_ERR.E0102) {

                    const redirectMap: { [key: string]: string } = {
                        E0102: "/regist-email",
                    };

                    const shouldRedirect = (code: string) => Object.prototype.hasOwnProperty.call(redirectMap, code);
                    if (shouldRedirect(errorCode)) {
                        navigate(redirectMap[errorCode], {
                            state: {
                                id: data_input.login_id ?? null,
                                dealer_cd: data_input.password ?? null
                            }
                        });
                    }
                }
            }
        } catch (error) {
            // Show error message or modal based on error code
            const error_code = error.response.data.error_code;
            const error_msg = error.response.data.error_msg;

            if (error_code === RESPONSE_ERR.WAI001) {
                setSystemError(ERR_MESSAGE.WAI001_MESSAGE);
            } else if (error_code == RESPONSE_ERR.WAS001) {
                setSystemError(ERR_MESSAGE.WAS001_MESSAGE);
            } else if (error_code == RESPONSE_ERR.WAI005) {
                setSystemError(ERR_MESSAGE.WAI005_MESSAGE);
            } else if (error_code == RESPONSE_ERR.WAI007) {
                setIsModalSignUpOpen(true);
            } else if (error_code == RESPONSE_ERR.WAS003) {
                setSystemError(ERR_MESSAGE.WAS003_MESSAGE);
            } else {
                setSystemError(ERR_MESSAGE.WAS001_MESSAGE);
            }
        } finally {
            setLoading(false);
        }


        // try {
        //     setIsModalLoginOpen(true)
        //     // setIsModalSignUpOpen(true);
        // } catch (err) {
        //     setIsModalSignUpOpen(false);
        // }
    }

    // Close sign-up modal
    const closeModal = () => {
        setIsModalSignUpOpen(false);
    }

    // Close login modal
    const closeModalLogin = () => {
        setIsModalLoginOpen(false);
    }

    // Close login but incorrect URL modal
    const closeModalLoginIncorrectURL = () => {
        setIsModalIncorrectURLLoginOpen(false);
    }

    // Display loading spinner when processing
    if (loading == true) {
        return <LoadingSpinner />;
    }

    // Render login form UI
    return (
        <Fragment>
            <div style={{ display: loading ? 'none' : 'block' }}>
                <div className="login-form" style={{ height: '100vh', display: 'flex' }}>
                    <div className="jumbotron-login">
                        <div className="logo justify-content-center" style={{ display: "flex" }}>
                            <img className="col-6" src={logoCCBJI} alt="logo" />
                            <img className="col-6" src={logoFVJ} alt="logo" />
                        </div>
                        <br />
                        <br />
                        <div className="logo justify-content-center" style={{ display: "flex" }}>
                            <img className="col-12" src={logo} alt="logo" />
                        </div>
                        <br />
                        <Text className="pt-2" style={{ color: "Red" }}>{showSystemError || ""}</Text>

                        <br className="d-none d-lg-block" />
                        <br className="d-none d-lg-block" />

                        <br className="d-block d-lg-none" />
                        <br className="d-block d-lg-none" />

                        <div className="main" >
                            <ul className="list-group list-group-flush">
                                <Form form={form}
                                    name="login"
                                    layout="vertical"
                                    onFinish={Ok}
                                    size="large"
                                    validateTrigger="onSubmit"
                                >
                                    <span className="font-weight-normal" >IDを入力してください（50から始まる10桁のお客様固有番号になります）</span>
                                    <Form.Item
                                        name="id"
                                        rules={[
                                            { required: true, message: "IDが入力必須" },
                                        ]}
                                    >
                                        <Input className='input-primary' ref={inputRef} autoComplete="false"
                                            maxLength={10} />
                                    </Form.Item>

                                    <span className="font-weight-normal" >パスワードを入力してください</span>
                                    <Form.Item
                                        name="password"
                                        rules={[
                                            { required: true, message: "パスワードが入力必須" },
                                        ]}
                                    >
                                        <Input.Password className='input-primary' maxLength={32} autoComplete="false" />
                                    </Form.Item>
                                    <div className="d-flex justify-content-center mt-4 mb-1">
                                        <a role="link" tabIndex={0} className="text-primary link-home text-center" onClick={goToResetPassword}><u>パスワードを忘れた方はコチラ</u></a>
                                    </div>
                                    <Form.Item className="d-flex justify-content-center mb-1">
                                        <Button variant="solid" className="bg-red-common login-btn" htmlType="submit" size={size} block>
                                            <Text className="text-white">ログイン</Text> <br />
                                            <Text className="text-white">（メールアドレス登録済の方）</Text>
                                        </Button>
                                    </Form.Item>
                                    <div className="d-flex justify-content-center mb-1">
                                        <a role="link" tabIndex={0} className="text-primary link-home text-center" onClick={goToHelpPC}><u>IDを忘れた方はコチラ</u></a>
                                    </div>
                                    <Form.Item className="d-flex justify-content-center">
                                        <Button variant="solid" className="btn btn-primary signup-btn" onClick={Signup} size={size} block>
                                            <Text className="text-white">初回登録</Text> <br />
                                            <Text className="text-white">（メールアドレス未登録の方）</Text>
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </ul>
                        </div>
                    </div>
                </div>
                {/* Modal for already registered message */}
                <Modal centered
                    title={titleSignUpOpen}
                    open={isModalSignUpOpen}
                    closeIcon={false}
                    footer={[
                        <Button key="back"
                            className="notification-modal"
                            onClick={closeModal}>
                            OK
                        </Button>,
                    ]}
                >
                </Modal>

                {/* Modal for incomplete registration message */}
                <Modal centered
                    title={titleLoginOpen}
                    open={isModalLoginOpen}
                    closeIcon={false}
                    footer={[
                        <Button key="back"
                            className="notification-modal"
                            onClick={closeModalLogin}>
                            OK
                        </Button>,
                    ]}
                >
                </Modal>

                {/* Modal for complete registration message but incorrect URL token */}
                <Modal centered
                    title={titleIncorrectURLLoginOpen}
                    open={isModalIncorrectURLLoginOpen}
                    closeIcon={false}
                    footer={[
                        <Button key="back"
                            className="notification-modal"
                            onClick={closeModalLoginIncorrectURL}>
                            OK
                        </Button>,
                    ]}
                >
                </Modal>
            </div>
        </Fragment>
    );
};

export default Login;

